import React, { FC, useId } from "react";

import {
  BackgroundThumbnailImage,
  DropDownField,
  Label,
  TextArea,
  TextField,
} from "~/components/atoms";
import {
  LabelWithCheckBox,
  LabelWithRadioButton,
} from "~/components/molecules";

import {
  AttachFileType,
  EmployeeConditionSurveyQuestionType,
  EmployeeSurveyQuestionType,
} from "~/domains";

type PropsType = {
  questionId: string;
  options: { id: string; option: string; file?: AttachFileType }[];
  answers: {
    questionId: string;
    optionId?: string;
    answer?: string;
  }[];
  onChangeAnswer?: (value: string | undefined, questionId: string) => void;
  required?: boolean;
};

const RadioButtonQuestion: FC<PropsType> = ({
  questionId,
  options,
  answers,
  onChangeAnswer,
  required,
}) => {
  const uniqueId = useId();
  const isAnswered = answers.some((a) => a.questionId === questionId);
  const hasFile = options.some((option) => option.file);

  return (
    <div
      className={`space-y-4 items-start ${hasFile ? "grid grid-cols-1 gap-2 miniTablet:[grid-template-columns:var(--grid-template-columns)]" : ""}`}
      style={
        hasFile
          ? ({
              "--grid-template-columns": `repeat(${options.length}, minmax(0, 1fr))`,
            } as React.CSSProperties)
          : undefined
      }
    >
      <input
        type="text"
        className="sr-only"
        name={`question-radiobutton-${questionId}-${uniqueId}`}
        required={required}
        value={isAnswered ? "true" : undefined}
      />
      {options.map((option) => {
        const isChecked = answers.some(
          (a) => a.questionId === questionId && a.optionId === option.id,
        );
        return (
          <div
            key={option.id}
            className={`flex miniTablet:flex-col flex-row cursor-pointer ${hasFile ? "items-center" : "items-start"}`}
            onClick={() => onChangeAnswer?.(option.id, questionId)}
          >
            {option.file && (
              <img
                src={option.file.url}
                alt={option.option}
                className={
                  "miniTablet:w-full w-1/5 miniTablet:h-full h-1/5 miniTablet:p-8 p-2 object-contain"
                }
              />
            )}
            <LabelWithRadioButton
              labelText={option.option}
              disabled={!onChangeAnswer}
              name={`question-radiobutton-${questionId}-option-${option.id}-${uniqueId}`}
              value={option.id}
              checked={isChecked}
              onChange={(e) => onChangeAnswer?.(e.target.value, questionId)}
              className="w-5 h-5"
            />
          </div>
        );
      })}
      {isAnswered && onChangeAnswer && (
        <div className="w-full flex justify-end col-span-full">
          <button
            type="button"
            className="text-secondary-600 text-sm underline"
            onClick={() => onChangeAnswer(undefined, questionId)}
          >
            選択を解除
          </button>
        </div>
      )}
    </div>
  );
};

const CheckBoxQuestion: FC<PropsType> = ({
  questionId,
  options,
  answers,
  onChangeAnswer,
  required,
}) => {
  const uniqueId = useId();
  const isAnswered = answers.some((a) => a.questionId === questionId);
  return (
    <div className="space-y-4">
      <input
        type="text"
        className="sr-only"
        name={`question-checkbox-${questionId}-${uniqueId}`}
        required={required}
        value={isAnswered ? "true" : undefined}
      />
      {options.map((option) => (
        <LabelWithCheckBox
          key={option.id}
          labelText={option.option}
          name={`question-checkbox-${questionId}-option-${option.id}-${uniqueId}`}
          value={option.id}
          labelEnd
          disabled={!onChangeAnswer}
          checked={answers.some(
            (a) => a.questionId === questionId && a.optionId === option.id,
          )}
          onChange={(e) => onChangeAnswer?.(e.target.value, questionId)}
        />
      ))}
    </div>
  );
};

const SelectBoxQuestion: FC<PropsType> = ({
  questionId,
  options,
  answers,
  onChangeAnswer,
  required,
}) => {
  const uniqueId = useId();
  return (
    <DropDownField
      isClearable={false}
      name={`question-select-${questionId}-${uniqueId}`}
      placeholder="選択してください"
      options={options.map((option) => ({
        value: option.id,
        label: option.option,
      }))}
      isDisabled={!onChangeAnswer}
      value={{
        value: answers.find((a) => a.questionId === questionId)?.optionId || "",
        label:
          options.find(
            (option) =>
              option.id ===
              answers.find((a) => a.questionId === questionId)?.optionId,
          )?.option || "",
      }}
      onChange={(newValue) =>
        onChangeAnswer?.(newValue?.value || "", questionId)
      }
      required={required}
    />
  );
};

const TextFieldQuestion: FC<PropsType> = ({
  answers,
  questionId,
  onChangeAnswer,
  required,
}) => {
  const uniqueId = useId();
  return (
    <TextField
      type="text"
      name={`question-${questionId}-${uniqueId}`}
      disabled={!onChangeAnswer}
      value={answers.find((a) => a.questionId === questionId)?.answer || ""}
      placeholder="回答を入力"
      onlyBottomBorder
      onChange={(e) => onChangeAnswer?.(e.target.value, questionId)}
      required={required}
    />
  );
};

const TextAreaQuestion: FC<PropsType> = ({
  answers,
  questionId,
  onChangeAnswer,
  required,
}) => {
  const uniqueId = useId();
  return (
    <TextArea
      name={`question-text-${questionId}-${uniqueId}`}
      disabled={!onChangeAnswer}
      value={answers.find((a) => a.questionId === questionId)?.answer || ""}
      placeholder="回答を入力"
      onChange={(e) => onChangeAnswer?.(e.target.value, questionId)}
      required={required}
    />
  );
};

type QuestionPropsType = Omit<PropsType, "questionId"> & {
  question:
    | Omit<EmployeeConditionSurveyQuestionType, "employeeConditionSurveyId">
    | Omit<EmployeeSurveyQuestionType, "employeeSurveyId">;
  displayImage?: boolean;
};

export const EmployeeSurveyQuestionDetail: FC<QuestionPropsType> = ({
  question,
  displayImage = false,
  ...props
}) => {
  const Field = {
    radio_button: (
      <RadioButtonQuestion
        questionId={question.id}
        required={question.required}
        {...props}
      />
    ),
    check_box: (
      <CheckBoxQuestion
        questionId={question.id}
        required={question.required}
        {...props}
      />
    ),
    select_box: (
      <SelectBoxQuestion
        questionId={question.id}
        required={question.required}
        {...props}
      />
    ),
    text_field: (
      <TextFieldQuestion
        questionId={question.id}
        required={question.required}
        {...props}
      />
    ),
    text_area: (
      <TextAreaQuestion
        questionId={question.id}
        required={question.required}
        {...props}
      />
    ),
  }[question.questionType.type];

  return (
    <div className="w-full pt-4 pb-7 px-4 rounded space-y-4 shadow">
      <Label
        htmlFor={question.id}
        labelText={question.question}
        required={question.required}
        className="text-secondary-900 font-semibold"
      />
      {displayImage && question.file && (
        <div className="flex justify-center relative">
          <BackgroundThumbnailImage
            bgImageUrl={question.file.url}
            className="w-full"
          />
        </div>
      )}
      {Field}
    </div>
  );
};
